import React from 'react';
import styled, { createGlobalStyle, css } from "styled-components";
import { ToastContainer } from "react-toastify";
import { darkLegacyOverrides, darkTokens, lightTokens } from "../theme/tokens";

/*
 * Inter for button labels, optically centred.
 *
 * Inter centres a line on its capital letters, so a mostly lower-case label
 * ("Create campaign") sat below the icon beside it; moving it so the lower
 * case was centred instead left the capitals above the icon. This is the
 * same font (the same Google Fonts files as the <link> in public/index.html)
 * with its ascent and descent shifted halfway between the two, which keeps
 * an icon level with the label as a whole (within half a pixel, measured on
 * Create campaign, New workspace and Send invite). The line's total height is
 * unchanged, so no button changes size. If Google moves Inter to a new
 * version, refresh the URLs from that same stylesheet.
 */
const buttonFontFaces = css`
  /* cyrillic-ext */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvvYwYL8g.woff2) format('woff2');
    unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* cyrillic */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvmYwYL8g.woff2) format('woff2');
    unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* greek-ext */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvuYwYL8g.woff2) format('woff2');
    unicode-range: U+1F00-1FFF;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* greek */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvhYwYL8g.woff2) format('woff2');
    unicode-range: U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* vietnamese */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvtYwYL8g.woff2) format('woff2');
    unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcvsYwYL8g.woff2) format('woff2');
    unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
  /* latin */
  @font-face {
    font-family: 'Inter Button';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v20/UcCo3FwrK3iLTcviYwY.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    ascent-override: 92.4%;
    descent-override: 28.7%;
    line-gap-override: 0%;
  }
`;

export const Styling = css`
  ${buttonFontFaces}
  :root {
    /* Design tokens (src/theme/tokens.js). Prefer these over raw hex in any new
       component — they are what makes a surface theme-aware. */
    ${lightTokens}

    /* variables */
    --light-gray: #9d9d9d;
    --gray: #f9f9f9;
    --gray-50: #cfcfcf;
    --gray-100: #f1f1f1;
    --gray-150: #d9d9d9;
    --gray-200: #f2f3f5;
    --gray-250: #e2e3e6;
    --gray-300: #777777;
    --gray-350: #EFEFEF;
    --gray-400: #E6E7EC;
    --gray-450: #eaeaea;
    --table-border: rgba(74, 85, 104, 0.1);
    --white: #fff;
    --off-white: #fffaf3;
    --text-color: #8d8d8d;
    --text-color-50: #313131;
    --black: #000;
    --dark: #505050;
    --primary: #1b1f3c;
    --primary-50: #6d91b9;
    --primary-100: #eaf2ff;
    --secondary: #222e44;
    --blue: #2783ec;
    --light: #f5faf9;
    --light-50: #cdcdcd;
    --success: #419400;
    --danger: #fe3839;
    --yellow: #ffb302;
    --body-bg: #fafcfe;
    --green: #3ea49d;
    --green-50: #0da06a;
    --green-100: #007436;
    --turquoise: #4ecdc4;
    --toastify-color-success: #408f8c;
    --toastify-icon-color-success: #408f8c;
    --toastify-color-error: rgba(215, 65, 32, 1);
    --toastify-icon-color-error: rgba(215, 65, 32, 1);
    --toastify-font-family: "DM Sans", sans-serif;

    /* Same stack as the Remix reference's --font-sans. */
    --base-font-sans-serif: "Inter", ui-sans-serif, system-ui, -apple-system,
      "Segoe UI", Roboto, sans-serif;
    --base-font-serif: "Inter", sans-serif;
    --roboto-font-serif: "Roboto", sans-serif;

    /* Body */
    --base-text-color: var(--light-gray);
    --base-background-color: var(--white);
    --font-size-base: 16px;
    --line-height-base: 20px;
    --base-font-family: var(--base-font-sans-serif);
    --base-min-width: 375px;

    /*  Links */
    --link-color: var(--primary);

    /*  Forms */
    --form-element-padding: 0.75rem 0.9375rem;
    --form-element-padding-lg: 20px 30px;
    --form-element-border-color: var(--gray);
    --form-element-focus-border-color: var(--black);
    --placeholder-color: var(--gray);

    /* Headings */
    --h1-font-size: 3.125rem; /* 50px */
    --h2-font-size: 1.5625rem; /* 30px */
    --h3-font-size: 1.25rem; /* 20px */
    --h4-font-size: 1.125rem; /* 18px */
    --h5-font-size: 16px; /* 16px */
    --h6-font-size: 0.9375rem; /* 15px */

    --headings-font-family: var(--base-font-family);
    --headings-color: var(--text-color);

    --animation-speed: 0.3s;

    --sm: 576px;
    --md: 768px;
    --lg: 992px;
    --xl: 1200px;
    --xxl: 1440px;

    --onboarding-brand-gradient: #000;
    --onboarding-shadow-soft: 0 4px 20px -4px rgba(0, 0, 0, 0.08);
    --onboarding-shadow-elevated: 0 8px 30px -8px rgba(37, 128, 243, 0.25);
    --onboarding-shadow-continue-hover: 0 20px 50px -12px rgba(37, 128, 243, 0.45);
    --onboarding-premium-bg:
      radial-gradient(ellipse 60% 50% at 85% 10%, rgba(37, 128, 243, 0.18), transparent 60%),
      radial-gradient(ellipse 70% 60% at 50% 50%, rgba(183, 75, 255, 0.14), transparent 65%),
      radial-gradient(ellipse 50% 40% at 20% 95%, rgba(255, 138, 122, 0.12), transparent 60%);
    --onboarding-glass-bg: rgba(255, 255, 255, 0.55);
    --onboarding-glass-border: 1px solid rgba(255, 255, 255, 0.7);
    --onboarding-glass-shadow:
      0 1px 0 0 rgba(255, 255, 255, 0.9) inset,
      0 20px 60px -20px rgba(37, 128, 243, 0.18),
      0 10px 40px -20px rgba(183, 75, 255, 0.15);
  }

  /* Dark palette. ThemeProvider sets data-theme and .dark on <html>; both are
     matched so either selector works from anywhere. */
  [data-theme='dark'],
  html.dark {
    ${darkTokens}
    ${darkLegacyOverrides}
  }

  /* ThemeProvider adds this to <html> for the single frame in which the palette
     swaps, then removes it. Every token flips at once instead of each element
     easing at its own duration, which is what made the change look like it was
     tearing. Hover and interaction transitions are unaffected either side of
     that frame. */
  html.theme-switching,
  html.theme-switching *,
  html.theme-switching *::before,
  html.theme-switching *::after {
    transition: none !important;
  }

  html {
    font-size: 100%;
  }

  * {
    margin: 0;
    padding: 0;
    scroll-behavior: smooth;
    box-sizing: border-box;
  }

  *:before,
  *:after {
    box-sizing: inherit;
  }

  body {
    font: 14px/18px var(--base-font-sans-serif);
    color: var(--text-color);
    font-weight: 400;
    background: var(--hd-background);
    min-width: var(--base-min-width);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    @media (min-width: 1440px) {
      font: var(--font-size-base) / var(--line-height-base)
        var(--base-font-sans-serif);
    }

    /* &.aside-active {
      overflow: hidden !important;

      .overlay {
        visibility: visible;
        opacity: 1;
      }
    } */
  }

  .overlay {
    visibility: hidden;
    opacity: 0;
    transition: all ease-in-out 0.3s;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 5;
  }

  .activate-modal {
    .message {
      display: block;
      font-size: 20px;
      line-height: 24px;
      font-weight: 500;
      color: var(--text-color-50);
      text-align: center;
      margin: 0 0 25px;
    }

    .btn-holder {
      display: flex;
      justify-content: center;
      gap: 10px;
    }
  }

  .data-loader-holder{
    width: 100%;
    height: calc(100vh - 80px);
    transition: all 0.350s ease-in-out;
    
    .trial-banner-visible & {
      height: calc(100vh - 130px);

    }
  }

  .offer-wrapper {
    .filter-wrap {
      padding: 0 15px 0;
      border-top: 0;

      .title {
        display: block;
        font-size: 20px;
        line-height: 24px;
        font-weight: 600;
        text-transform: capitalize;
        color: var(--black);
        padding-right: 25px;
      }
    }
  }

  .form-hook-country-field {
    .label {
      display: block;
      color: var(--text-color-50);
      margin-bottom: 8px;
    }

    .react-select__control {
      border-radius: 60px;
      height: 40px;
      margin: 0 0 16px;
      box-shadow: none;

      &.react-select__control--is-focused,
      &.react-select__control--menu-is-open {
        border-color: var(--turquoise);
      }
    }

    .react-select__menu {
      z-index: 9999;
    }

    .flag-holder {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    img {
      width: 20px;
      height: 20px;
      border-radius: 50px;
    }

    .react-select__indicator-separator {
      display: none;
    }
  }

  .grd-color {
    color: #000;
    -webkit-text-fill-color: #000;
  }


  .container {
    max-width: 1330px;
    margin: 0 auto;
    padding: 0 15px;
  }

  input:-webkit-autofill,
  textarea:-webkit-autofill {
    background-color: transparent !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  input:-webkit-autofill:focus,
  textarea:-webkit-autofill:focus {
    background-color: transparent !important;
  }
  /*  ---------------- Helpers ---------------- */
  .success-bg {
    background: #419400;
  }
  .success-bg-50 {
    background: rgba(65, 148, 0, 0.1);
  }
  .danger-bg {
    background: rgba(215, 65, 32, 1);
  }
  .danger-bg-50 {
    background: rgba(215, 65, 32, 0.1);
  }
  .primary-bg {
    background: rgba(53, 91, 133, 1);
  }
  .primary-bg-50 {
    background: rgba(53, 91, 133, 0.1);
  }
  .secondary-bg {
    background: rgba(157, 157, 157, 1);
  }
  .secondary-bg-50 {
    background: rgba(157, 157, 157, 0.1);
  }

  .Toastify__toast-container {
    width: 320px;
  }
  .Toastify__toast-container--top-center {
    width: 480px;
  }
  .Toastify__toast {
    box-shadow: none;
  }

  /*  ---------------- Helpers ---------------- */
  .not-found {
    display: block;
    max-width: max-content;
    background: rgb(255, 235, 235);
    color: #d74120;
    padding: 15px 20px;
    border-radius: 10px;
    min-width: 200px;
    text-align: center;
    margin: 20px auto 0;
  }

  input {
    font-family: var(--base-font-sans-serif);
  }

  .highcharts-range-selector-group,
  .highcharts-navigator {
    display: none;
  }

  .flex-block {
    display: flex;
    gap: 20px;
  }
  .mt-2 {
    margin-top: 30px;
  }
  .link {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 17px;
    font-weight: 700;
    color: var(--success);
  }

  img {
    max-width: 100%;
    height: auto;
    vertical-align: top;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  textarea {
    resize: vertical;
    vertical-align: top;
    font-family: var(--base-font-sans-serif);
  }

  button,
  input[type="button"],
  input[type="reset"],
  input[type="file"],
  input[type="submit"] {
    cursor: pointer;
  }

  form,
  fieldset {
    margin: 0;
    padding: 0;
    border-style: none;
  }
  a {
    color: #313131;
    text-decoration: none;
    cursor: pointer;
  }

  input[type="search"]::-webkit-search-decoration,
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-results-button,
  input[type="search"]::-webkit-search-results-decoration {
    display: none;
  }

  .align-middle {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* !important because most buttons set their own font-family (inherit or "Inter"). */
  button {
    font-family: 'Inter Button', var(--base-font-sans-serif) !important;
    padding: 0;
    border: none;
    background: none;
    outline: none;
  }
  .cloud-iframe-wrapper {
    text-align: center;

    iframe {
      width: 100%;
      height: 570px;
      margin: 0 0 20px;
    }
  }
  .react-datepicker-popper {
    left: 10px !important;

    .react-datepicker {
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
    }
  }
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background: var(--blue) !important;
  }

  h1,
  .h1,
  h2,
  .h2,
  h3,
  .h3,
  h4,
  .h4,
  h5,
  .h5,
  h6,
  .h6 {
    margin: 0 0 10px;
    color: var(--secondary);
    font-weight: 600;
  }

  h1,
  .h1 {
    font-size: 22px;
    line-height: 26px;
    text-transform: capitalize;

    @media (min-width: 576px) {
      font-size: 24px;
      line-height: 28px;
    }
  }

  h2,
  .h2 {
    font-size: 18px;
    line-height: 20px;
    text-transform: capitalize;

    @media (min-width: 576px) {
      font-size: 20px;
      line-height: 24px;
    }
  }

  h3,
  .h3 {
    font-size: 18px;
    line-height: 20px;
    text-transform: capitalize;

  }

  p {
    color: var(--base-text-color);
    margin: 0 0 10px;
    /* Never leave one word alone on a paragraph's last line. */
    text-wrap: pretty;

    &:last-child {
      margin: 0;
    }
  }
  .notfound {
    max-width: max-content;
    margin: 0 auto;
    text-align: center;
    padding: 12px 15px;
    color: var(--white);
    border-radius: 8px;
    background: rgba(215, 65, 32, 1);
  }

  .domain-public-dkim {
    display: flex;
    justify-content: center;
    gap: 5px;

    .text {
      -webkit-text-security: disc;
      text-security: disc;
      font-family: "password";
      color: var(--light-gray);
    }
  }
  .leads-modal {
    position: relative;
    text-align: center;
    padding: 0 0 20px;

    .heading {
      display: block;
      font-size: 25px;
      line-height: 29px;
      color: var(--text-color-50);
      margin: 0 0 15px;
    }

    p{
      color: var(--text-color-50);
    }
    .lock-text{
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .heading-text {
    margin-bottom: 30px;

    .email-text {
      display: block;
      font-size: 15px;
      line-height: 19px;

      .otp-mail {
        font-style: italic;
        font-weight: 700;
        color: var(--primary);
      }
    }
  }

  .campaign-launched {
    height: calc(100vh - 70px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .badge {
    min-width: 100px;
    font-size: 12px;
    line-height: 16px;
    padding: 6px 15px;
    margin: 0 auto;
    border-radius: 50px;
    text-align: center;
    max-width: max-content;
    text-transform: capitalize;

    &.new,
    &.paused,
    &.submitted {
      color: #a4abb6;
      background: rgba(164, 171, 182, 0.12);
    }
    &.done {
      color: #a4abb6;
      border: 1px solid rgba(0, 0, 0, 0.31);
    }
    &.uploading {
      color: #000;
      border: 1px solid rgba(0, 0, 0, 0.31);
    }

    &.replied,
    &.success {
      color: #47c500;
      background: rgba(78, 210, 3, 0.12);
    }

    &.unsubscribed,
    &.draft {
      color: #ffc831;
      background: rgba(255, 249, 234, 1);
    }

    &.blocked,
    &.pending,
    &.failed {
      color: #fe3839;
      background: rgba(254, 56, 57, 0.12);
    }

    &.bounced {
      color: #a4abb6;
      background: rgba(164, 171, 182, 0.12);
    }

    &.completed,
    &.done {
      color: #10B981;
      background: rgba(16, 185, 129, 0.12);
    }

    &.opend,
    &.clicked,
    &.running {
      color: #2cccff;
      background: rgba(44, 204, 255, 0.12);
    }

    &.right {
      margin-left: auto;
    }

    &.center {
      margin: 0 auto;
    }
  }

.gradient-icon {
  background: linear-gradient(90deg, #367EEE -19.76%, #B74BFF 45.75%, #FEB508 111.26%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  display: inline-flex;
}

svg.gradient-icon,
.gradient-icon svg {
  fill: url(#houdiny-icon-gradient);
  color: transparent;
}

svg.gradient-icon path,
.gradient-icon svg path {
  fill: url(#houdiny-icon-gradient);
}

  .flex-content {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;

    @media (min-width: 1450px) {
      gap: 20px;
    }
  }
  
  .avatar-wrap {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 50px;
    border: 1px solid #ddd;
    user-select: none;
    
    @media (min-width: 1450px) {
      width: 36px;
      height: 36px;
      margin-left: 10px;
    }
  }
  .text-wrap {
    flex-grow: 1;
  }

  .th-label-box {
    position: relative;
    .label {
      gap: 65px;
    }
  }

  .progress-bar.in-progress .progress-fill {
    background: #317bff; /* blue */
  }

  .progress-bar.failed .progress-fill {
    background: #ffa2a4; /* Red */
  }

  .progress-bar.running .progress-fill {
    background: #ee8345; /* orange */
  }

  /************* custom scrollbar styles ************/

  /* This will work on Firefox */
  * {
    /* scrollbar-width: thin; */
    scrollbar-color: var(--text-color-50);
  }

  /*
   * Targtes on Chrome, Edge, and Safari. Elements marked .native-scrollbar keep
   * the browser's own scrollbar, as version3's menus do.
   */
  :where(:not(.native-scrollbar))::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    color: var(--primary);
  }

  :where(:not(.native-scrollbar))::-webkit-scrollbar-track {
    border-radius: 30px;
    background: var(--gray-150);
  }

  :where(:not(.native-scrollbar))::-webkit-scrollbar-thumb {
    background: var(--text-color-50);
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.03);
    border-radius: 30px;
  }

  /* Remove Arrows/Spinners from input type number */

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type="number"] {
    appearance: textfield;
  }

  .react-datepicker {
    font-family: var(--base-font-sans-serif);
    border: none;
    box-shadow:
      0 2.8px 2.2px rgba(0, 0, 0, 0.034),
      0 6.7px 5.3px rgba(0, 0, 0, 0.048),
      0 12.5px 10px rgba(0, 0, 0, 0.06),
      0 22.3px 17.9px rgba(0, 0, 0, 0.072),
      0 41.8px 33.4px rgba(0, 0, 0, 0.086),
      0 100px 80px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
  }
  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range {
    background-color: var(--primary);
    color: var(--white);
  }

  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background-color: var(--primary);
    color: var(--white);
  }

  .react-datepicker__day--keyboard-selected:hover,
  .react-datepicker__month-text--keyboard-selected:hover,
  .react-datepicker__quarter-text--keyboard-selected:hover,
  .react-datepicker__year-text--keyboard-selected:hover {
    background-color: var(--primary);
    color: var(--white);
  }

  .react-datepicker__day--in-selecting-range:not(
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--in-range
  ),
  .react-datepicker__month-text--in-selecting-range:not(
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--in-range
  ),
  .react-datepicker__quarter-text--in-selecting-range:not(
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--in-range
  ),
  .react-datepicker__year-text--in-selecting-range:not(
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--in-range
  ) {
    background-color: var(--primary);
    color: var(--white) !important;
    opacity: 0.5;
  }

  .react-datepicker__day--selected:hover,
  .react-datepicker__day--in-selecting-range:hover,
  .react-datepicker__day--in-range:hover,
  .react-datepicker__month-text--selected:hover,
  .react-datepicker__month-text--in-selecting-range:hover,
  .react-datepicker__month-text--in-range:hover,
  .react-datepicker__quarter-text--selected:hover,
  .react-datepicker__quarter-text--in-selecting-range:hover,
  .react-datepicker__quarter-text--in-range:hover,
  .react-datepicker__year-text--selected:hover,
  .react-datepicker__year-text--in-selecting-range:hover,
  .react-datepicker__year-text--in-range:hover {
    background-color: var(--primary);
    color: var(--white);
    opacity: 0.7;
  }
  .react-datepicker__close-icon {
    &:after {
      padding: 0 !important;
      line-height: 16px !important;
      display: block !important;
      background: var(--primary) !important;
    }
  }
  .react-datepicker__header {
    background: none;
    border: none;
    padding-bottom: 0;
    padding-top: 15px;
  }
  .react-datepicker__day-name {
    color: var(--light-gray);
    font-weight: bold;
  }
  .react-datepicker-popper {
    z-index: 9;
  }

  .date-btn {
    background: rgba(241, 241, 241, 1);
    color: var(--text-color);
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    height: 45px;
  }
  .add-user-btn {
    padding: 10px 14px;
    height: 45px;
    font-size: 14px;
    font-weight: 600;
  }

  .select-wrap {
    display: flex;
    align-items: start;
    flex-grow: 1;
    gap: 10px;
  }

  .dropdown-holder {
    position: relative;
    transition: 0.3s;
    color: #1c1c1c;
    background: #fafafa;
    
    button {
      border: 1px solid var(--gray-450);
      border-radius: 50px;
    }

    &.active {
      .dropdown {
        opacity: 1;
        visibility: visible;
      }

      .icon {
        transform: scale(-1);
      }
    }

    .icon {
      transition: 0.3s;
      transform: scale(1);
      flex-shrink: 0;
    }

    button {
      width: 100%;
      font-size: 12px;
      line-height: 15px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 13px 14px;

      span {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    span {
      display: block;
    }

    .dropdown {
      width: 100%;
      position: absolute;
      top: 100%;
      left: -1px;
      right: -1px;
      background: var(--white);
      z-index: 1;
      padding: 10px 0;
      margin-top: 7px;
      border: 1px solid var(--gray-450);
      border-radius: 10px;
      opacity: 0;
      visibility: hidden;
      transition: 0.3s;

      .option-holder {
        max-height: 300px;
        overflow-y: auto;
      }

      span {
        font-size: 14px;
        line-height: 15px;
        padding: 8px 10px;
        color: #575757;
        cursor: pointer;
        word-break: break-all;

        &:not(:last-child) {
          border-bottom: 1px solid var(--gray-250);
        }

        &:hover {
          background: #f3f3f7;
        }
      }
    }
  }


  /**
   * Anything not built yet: data-coming-soon on the control.
   *
   *   <button data-coming-soon aria-disabled="true" title="Coming soon" …>
   *
   * Deliberately NOT the disabled attribute. A disabled control fires no mouse
   * events in most browsers, so :hover never matches and the one thing the user
   * needs — being told why nothing happened — is exactly what stops working. The
   * handler is neutered in JS instead, aria-disabled tells assistive tech, and
   * the title is the fallback wherever a hover does not exist at all.
   *
   * Lives here rather than in each component so the three places that need it
   * say it identically, and so a fourth costs one attribute.
   */
  [data-coming-soon] {
    position: relative;
    opacity: 0.55;
    cursor: not-allowed;
  }

  /**
   * Below and centred by default.
   *
   * Above was wrong more often than it was right: these controls sit at the top
   * of a page (the mode toggle) or hard against an edge (the flow builder's
   * launcher, 16px from the left), so the bubble was either clipped by the panel
   * or pushed off the screen. There is nearly always room underneath. Where there
   * is not, data-coming-soon="right" / "left" / "above" moves it.
   */
  [data-coming-soon]::after {
    content: 'Coming soon';
    position: absolute;
    top: calc(100% + 7px);
    left: 50%;
    z-index: 60;
    padding: 4px 9px;
    border-radius: 7px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.4;
    white-space: nowrap;
    color: var(--hd-background, #fff);
    background: var(--hd-foreground, #18181b);
    box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.45);
    opacity: 0;
    transform: translate(-50%, -3px);
    pointer-events: none;
    transition:
      opacity 0.14s ease,
      transform 0.14s ease;
  }

  [data-coming-soon='above']::after {
    top: auto;
    bottom: calc(100% + 7px);
    transform: translate(-50%, 3px);
  }

  /* For a control against the left edge: the bubble opens into the page. */
  [data-coming-soon='right']::after {
    top: 50%;
    left: calc(100% + 9px);
    transform: translate(-3px, -50%);
  }

  [data-coming-soon='left']::after {
    top: 50%;
    left: auto;
    right: calc(100% + 9px);
    transform: translate(3px, -50%);
  }

  /* Focus counts as well as hover: the control is still reachable by keyboard,
     and a keyboard user gets the same answer a pointer user does. */
  [data-coming-soon]:hover::after,
  [data-coming-soon]:focus-visible::after {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  [data-coming-soon='above']:hover::after,
  [data-coming-soon='above']:focus-visible::after {
    transform: translate(-50%, 0);
  }

  [data-coming-soon='right']:hover::after,
  [data-coming-soon='right']:focus-visible::after,
  [data-coming-soon='left']:hover::after,
  [data-coming-soon='left']:focus-visible::after {
    transform: translate(0, -50%);
  }
`;

export const Wrapper = styled.div`
  overflow: hidden;
`;

export const LayoutWrapper = styled.div`
  width: 100%;
  padding: 118px 50px 0 360px;
  min-height: 100vh;
`;
export const WithoutLayoutWrapper = styled.div`
  width: 100%;
  padding: 40px;
  min-height: 100vh;
`;

const GlobalStyles = createGlobalStyle`
  ${Styling}
`;
export const HelperClasses = css`
  [class^="material-icons-"],
  [class*=" material-icons-"] {
    font-size: inherit;
  }
  [data-reach-menu-popover] {
    z-index: 50;
  }

  //rc picker style
  .rc-picker-dropdown {
    box-shadow: none;
  }
  .rc-picker-time-panel-cell {
    &.rc-picker-time-panel-cell-selected {
      background: var(--primary);
    }
  }
  .rc-picker-time-panel-column {
    &:not(:last-child) {
      border-right: 1px solid #ccc;
    }
  }
  .rc-picker-panel-container {
    background: #fff;
    border-radius: 2px;
    box-shadow:
      0 3px 6px -4px #0000001f,
      0 6px 16px #00000014,
      0 9px 28px 8px #0000000d;
  }
  .rc-picker-panel {
    background: #fff;
    border: none;
  }
  .rc-picker-range-separator {
    padding-left: 15px;
    padding-right: 15px;
    font-size: var(--font-size-xl);
    line-height: 1;
  }

  .rc-picker-dropdown .rc-picker-range-arrow {
    box-shadow: 2px -2px 6px #0000000f;
  }

  .rc-picker-dropdown .rc-picker-range-arrow:after {
    display: none;
  }
  .rc-picker-dropdown .rc-picker-range-arrow:before {
    border: 5px solid var(--white);
    border-color: var(--white) var(--white) transparent transparent;
  }
  .rc-picker-body {
    padding: 10px;
  }
  .rc-picker-header {
    padding: 5px 10px;
    border-bottom: 1px solid #ccc;
    gap: 6px;
  }
  .rc-picker-footer {
    background: none !important;
    border-top: 1px solid #ccc;
    padding: 5px 10px;
  }

  .rc-picker-datetime-panel .rc-picker-time-panel {
    border-left-color: #ccc;
  }
  .rc-picker-ok {
    button {
      background: var(--primary);
      color: var(--white);
      padding: 5px 10px;
      border-radius: 3px;
      font-size: var(--font-size-sm);
    }
  }
  .rc-picker-range .rc-picker-active-bar {
    background: var(--primary);
  }

  .rc-picker-cell-in-range > .rc-picker-cell-inner {
    background: rgba(52, 219, 161, 0.4);
  }

  .rc-picker-cell-range-start > .rc-picker-cell-inner,
  .rc-picker-cell-range-end > .rc-picker-cell-inner,
  .rc-picker-cell-selected > .rc-picker-cell-inner {
    background: var(--primary);
  }
  .rc-picker-cell-today > .rc-picker-cell-inner {
    border: 1px solid var(--primary);
  }
  .rc-picker-content thead th {
    font-size: var(--font-size-xs);
  }
  .rc-picker-cell-inner:hover {
    background: rgba(171, 233, 101, 0.4);
  }
  .rc-picker-dropdown {
    z-index: var(--z-55);
  }
  .rc-picker-header-view {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
    gap: 10px;
    button {
      &:hover {
        color: var(--primary);
      }
    }
  }

  .rc-picker-clear {
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    z-index: 2;
  }

  .star-rating {
    font-size: 24px !important;
  }

  .countrySelect {
    display: flex;
    align-items: center;
    gap: 10px;
    figure {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    img {
      display: block;
      width: 20px;
      height: 20px;
      object-fit: cover;
    }
  }
`;

export const StyledProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;

  .progress-bar {
    width: 100px;
    height: 14px;
    background-color: #e5e7eb;
    border-radius: 50px;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      border-radius: 50px;
      background: #7bf4c8;
      width: ${({ $width }) => ($width ? `${$width}%` : "0%")};

      background-image: ${({ $barLoading }) =>
    $barLoading
      ? `linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.35) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.35) 50%,
        rgba(255, 255, 255, 0.35) 75%,
        transparent 75%,
        transparent
      )`
      : "none"};

      background-size: 25px 25px;
      animation: move 3s linear infinite;
    }
  }

  @keyframes move {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 50px 50px;
    }
  }

  .progress-text {
    font-size: 13px;
    color: #333;
    min-width: 32px;
  }

  .progress-fill {
    height: 100%;
    border-radius: 50px;
    transition: width 0.3s ease;
  }

  /* Status-based colors */
  .progress-bar.completed,
  .progress-bar.succeeded {
    &::after {
      width: 100%;
    }
  }

  /* Use real $width so the fill matches .progress-text (50% here overrode 0% from props). */
  .progress-bar.running,
  .progress-bar.pending {
    &::after {
      width: ${({ $width }) => `${Math.min(100, Math.max(0, Number($width) || 0))}%`};
    }
  }

  .progress-bar.failed {
    background: rgba(254, 56, 57, 0.8);
  }
`;

export const StyledToastContainer = styled(ToastContainer)`
  z-index: 99999;

  .Toastify__toast {
    padding: 0;
    min-height: 0;
    border-radius: 8px;
    font-family: inherit;
  }
  .Toastify__toast--default {
    background: none;
  }
  .Toastify__toast-body {
    padding: 0;
  }
  .Toastify__close-button {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
  }
`;

export const StyledErrorFallback = styled.div`
  font-family: "DM Sans", sans-serif;
  height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  svg {
    width: 150px;
    height: 110px;
    margin: 0 0 10px;
  }

  h1 {
    font-size: 50px;
    line-height: 55px;
    margin: 0 0 15px;
  }

  .text {
    display: block;
    font-size: 20px;
    line-height: 24px;
    &:last-child {
      margin: 0 0 20px;
    }
  }

  .email {
    font-size: 18px;
    line-height: 22px;
    color: #2679f3;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const IconGradientDefs = () => (
  <svg aria-hidden="true" focusable="false" width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id="houdiny-icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#367EEE" />
        <stop offset="45.75%" stopColor="#B74BFF" />
        <stop offset="100%" stopColor="#FEB508" />
      </linearGradient>
    </defs>
  </svg>
);

export default GlobalStyles;
